/**
 * The realm as it travels.
 *
 * A room used to be several machines each running the whole realm and agreeing
 * to apply the same orders on the same tick. It is now one machine running the
 * realm and several drawing it, so what crosses the wire is the board itself:
 * this file is the only place that says what "the board" means.
 *
 * It is deliberately thin. `saveCampaign` already answers the hard question —
 * *what counts as state* — and answers it in step with `hashCampaign`, which is
 * the invariant `campaign/save.ts` opens by stating. Replication rides on that
 * rather than keeping a second field list beside it, so a field added to the
 * campaign reaches the wire the same day it reaches the save file, or it reaches
 * neither and the existing tests say so.
 *
 * Three scalars ride along outside the campaign. The Long Ledger's day is one of
 * them: the council reads it to decide whether the month has turned, and a
 * client that had the board but not the date would draw a realm whose seasons
 * never changed.
 *
 * Headless on purpose — the server imports this, and the server has no DOM.
 */
import { loadCampaign, saveCampaign, type CampaignSave } from '../sim/campaign/save.ts';
import type { World } from '../sim/world.ts';
import { diff, patch, type Json, type Patch } from './delta.ts';

/** The whole board, at one tick. */
export interface RealmState {
  tick: number;
  /** `World.calendarDay` — the Long Ledger's absolute day. */
  day: number;
  /** `World.calendarT` — how far through that day. */
  dayT: number;
  camp: CampaignSave;
}

/** What changed between two of them, and the tick it lands on. */
export interface RealmUpdate {
  tick: number;
  day: number;
  dayT: number;
  /** Absent when the board is unchanged — a paused room says nothing at all. */
  d?: Patch;
}

/** Read the board off the authoritative world. Deep — the caller may keep it. */
export function readRealm(w: World): RealmState {
  return {
    tick: w.tick,
    day: w.calendarDay,
    dayT: w.calendarT,
    camp: w.campaign ? saveCampaign(w.campaign) : ({} as CampaignSave),
  };
}

/** What to send a client that is already holding `prev`. */
export function realmUpdate(prev: RealmState, next: RealmState): RealmUpdate {
  const d = diff(prev.camp as unknown as Json, next.camp as unknown as Json);
  const out: RealmUpdate = { tick: next.tick, day: next.day, dayT: next.dayT };
  if (d) out.d = d;
  return out;
}

/**
 * Adopt a board whole, which is what joining a room is.
 *
 * The world itself is built locally from the room's recipe — the map is a pure
 * function of the seed, and shipping a hundred and forty thousand tiles that
 * both ends can generate would be the largest thing on the wire by an order of
 * magnitude. What cannot be derived is what has *happened*, and that is exactly
 * what this carries.
 */
export function adoptRealm(w: World, s: RealmState): void {
  const c = loadCampaign(s.camp);
  if (c) w.campaign = c;
  w.tick = s.tick;
  w.calendarDay = s.day;
  w.calendarT = s.dayT;
}

/**
 * Move a held board forward.
 *
 * In place, and that is the point of the delta rather than a nicety: the panels
 * reading this state hold on to it between frames, and the alternative — a fresh
 * campaign built out of `loadCampaign` ten times a second — would hand every one
 * of them a different object to no purpose.
 *
 * `playerNation` and `capital` are the two fields a client owns rather than
 * receives: they are *which of these crowns am I*, and the server plays nobody.
 * They never appear in a patch, because the server never changes them, so this
 * needs no special case — but it is why they must be set once after `adoptRealm`
 * and never read off the wire.
 */
export function applyRealmUpdate(w: World, u: RealmUpdate): void {
  if (u.d && w.campaign) patch(w.campaign as unknown as Json, u.d);
  w.tick = u.tick;
  w.calendarDay = u.day;
  w.calendarT = u.dayT;
}
